import type { OptimizationBlockState, ResumeOptimizationRun } from './types'

/** UI prep phase can show rotating copy for ~16s; worker crash leaves all blocks pending. */
export const OPTIMIZATION_STUCK_UX_MS = 3 * 60 * 1000

export function optimizationRunLooksStuck(
  run: ResumeOptimizationRun,
  startedAtMs: number | null,
  nowMs: number = Date.now()
): boolean {
  if (run.status !== 'pending' && run.status !== 'running') {
    return false
  }
  if (startedAtMs == null) {
    return false
  }
  if (nowMs - startedAtMs < OPTIMIZATION_STUCK_UX_MS) {
    return false
  }
  const blocks = run.blocks ?? []
  if (!blocks.length) {
    return true
  }
  return blocks.every((b) => b.status === 'pending')
}

export function stuckOptimizationSentryExtra(
  run: ResumeOptimizationRun,
  startedAtMs: number | null,
  nowMs: number = Date.now()
): Record<string, unknown> {
  const blocks = run.blocks ?? []
  return {
    optimization_run_id: run.id,
    resume_id: run.resume_id,
    run_status: run.status,
    elapsed_ms: startedAtMs != null ? nowMs - startedAtMs : null,
    blocks_total: blocks.length,
    blocks_pending: blocks.filter((b) => b.status === 'pending').length,
    blocks_running: blocks.filter((b) => b.status === 'running').length,
    blocks_done: blocks.filter((b) => b.status === 'done' || b.status === 'skipped').length,
  }
}

export function shouldReportStuckOptimizationToSentry(
  run: ResumeOptimizationRun,
  startedAtMs: number | null,
  alreadyReportedRunId: string | null,
  nowMs: number = Date.now()
): boolean {
  if (alreadyReportedRunId === run.id) {
    return false
  }
  return optimizationRunLooksStuck(run, startedAtMs, nowMs)
}

export function summarizeStuckBlocks(blocks: OptimizationBlockState[]): string {
  const pending = blocks.filter((b) => b.status === 'pending').length
  return `all ${pending} blocks still pending`
}
